import React, { useMemo, useCallback, useLayoutEffect } from 'react';
import { FormInstance, Col, Form, Row } from 'antd';
import { FormValues } from '../types';
import { isEqual, pick, omit, noop } from 'lodash';
import {
  Button,
  FormItem,
  Input,
  PageCard,
  Title,
  Select,
  Text,
  UpdatePhoneNumber,
  UploadProfilePhotoModal,
} from '@vette/ui-components';
import {
  useAddPushNotificationTokenMutation,
  useDisablePushNotificationMutation,
} from '@vette/data-access';
import { firebase, hooks, message } from '@vette/frontend-utils';
import { residences } from './constants';
import * as S from './styles';

type Props = {
  userId: string;
  form: FormInstance<FormValues>;
  initialValues: FormValues & {
    photoOriginalUrl?: string | null;
    attributes?:
      | {
          __typename?: 'UserAttributeValue';
          name?: string | null;
          values?: (string | null | undefined)[] | null;
        }[]
      | null;
  };
  onSubmit: (values: FormValues) => void;
  submitting: boolean;
  stripeAccountConnected?: boolean;
  pushNotificationsEnabled?: boolean;
};

export const SettingsForm: React.FC<Props> = ({
  userId,
  form,
  onSubmit,
  initialValues,
  submitting,
  pushNotificationsEnabled,
}) => {
  const photo = Form.useWatch('photoOriginal', form);
  const firstName = Form.useWatch('firstName', form);
  const { isVetter, isClient, isAdmin, isClientManager } = hooks.useRole();
  const { visible, open, close } = hooks.useModalControlProps();

  useLayoutEffect(() => {
    form.validateFields();
  }, []);

  const [addPushNotificationToken] = useAddPushNotificationTokenMutation({
    optimisticResponse: {
      __typename: 'Mutation',
      userAddPushNotificationToken: {
        id: userId,
        pushNotificationsEnabled: true,
        __typename: 'User',
      },
    },
    onError: errors => {
      console.error(errors);
      message.error(
        'Failed to enable push notifications. Please adjust your browser settings to enable notifications again.'
      );
    },
  });

  const [disablePushNotification] = useDisablePushNotificationMutation({
    optimisticResponse: {
      __typename: 'Mutation',
      userDisablePushNotification: {
        id: userId,
        pushNotificationsEnabled: false,
        __typename: 'User',
      },
    },
    onError: errors => {
      console.error(errors);
      message.error('Failed to disable push notifications.');
    },
  });

  const onFinish = (values: unknown) => {
    onSubmit({ ...(values as FormValues) });
  };

  const updatePushNotificationEnabled = useCallback(async () => {
    if (!pushNotificationsEnabled) {
      const token = await firebase.messaging.obtainPushNotificationToken();
      if (token) {
        await addPushNotificationToken({
          variables: {
            userId,
            token,
          },
        });
      } else {
        message.error(
          `It looks like notification permission for this site has been disabled. Please check the browser permissions and allow notifications for this site.`
        );
      }
    } else {
      await disablePushNotification({
        variables: {
          userId,
        },
      });
    }
  }, [
    pushNotificationsEnabled,
    addPushNotificationToken,
    userId,
    disablePushNotification,
  ]);

  const mappedInitialValues = useMemo(() => {
    if (!initialValues) {
      return initialValues;
    }
    const userSetLanguage = initialValues.attributes?.find(
      ({ name }) => name?.toLowerCase() === 'language'
    );

    return {
      ...(!isClientManager && {
        language: userSetLanguage ? userSetLanguage.values : undefined,
      }),
      photoOriginal: initialValues.photoOriginalUrl
        ? { url: initialValues.photoOriginalUrl }
        : undefined,
      ...(pick(initialValues, [
        'firstName',
        'lastName',
        'email',
        'phone',
        'residence',
        'zipCode',
      ] as Array<keyof FormValues>) as FormValues),
    };
  }, [initialValues, isClientManager]);

  return (
    <S.Form form={form} onFinish={onFinish} initialValues={mappedInitialValues}>
      <PageCard
        title="Settings"
        headerActions={
          <FormItem shouldUpdate>
            {() => {
              const currentValues = form.getFieldsValue();
              return (
                <Button
                  block
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={isEqual(
                    {
                      ...omit(currentValues, [
                        'photoOriginal',
                        'photoHigh',
                        'photoLow',
                      ]),
                      photoOriginal:
                        currentValues.photoOriginal?.url.split('?')?.[0],
                    },
                    {
                      ...omit(mappedInitialValues, ['photoOriginal']),
                      photoOriginal:
                        mappedInitialValues.photoOriginal?.url.split('?')?.[0],
                    }
                  )}
                >
                  Save
                </Button>
              );
            }}
          </FormItem>
        }
      >
        <S.Content>
          <FormItem noStyle ignoreNormalize name="photoLow" />
          <FormItem noStyle ignoreNormalize name="photoHigh" />
          <FormItem noStyle ignoreNormalize name="photoOriginal" />
          <UploadProfilePhotoModal
            firstName={firstName}
            initialPhotoUrl={photo?.url}
            onUpload={uploadData => {
              form.setFieldsValue({
                photoLow: uploadData.photoLow,
                photoHigh: uploadData.photoHigh,
                photoOriginal: uploadData.photoOriginal,
              });
              close();
            }}
            visible={visible}
            onClose={close}
          />
          <S.Section>
            <S.TitleRow justify="space-between" align="middle">
              <Title level={2}>Personal Information</Title>
            </S.TitleRow>
            <S.Row gutter={72}>
              <Col span={12}>
                <S.Row gutter={24}>
                  <Col span={12}>
                    <FormItem
                      label="First name"
                      name="firstName"
                      validateTrigger={['onChange']}
                      rules={[
                        { required: true, message: 'First name is required' },
                      ]}
                    >
                      <Input size="large" />
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem
                      label="Last name"
                      name="lastName"
                      validateTrigger={['onChange']}
                      rules={[
                        { required: true, message: 'Last name is required' },
                      ]}
                    >
                      <Input size="large" />
                    </FormItem>
                  </Col>
                </S.Row>
                <S.Row gutter={24} $marginTop={24}>
                  <Col span={24}>
                    <FormItem
                      noStyle={isClient || isAdmin}
                      label="Residence"
                      name="residence"
                      validateTrigger={['onChange']}
                      rules={[
                        {
                          required: isVetter,
                          message: 'Residence is required',
                        },
                      ]}
                    >
                      {isVetter && (
                        <Select
                          id="residence"
                          option={residences}
                          size="large"
                        />
                      )}
                    </FormItem>
                  </Col>
                </S.Row>
                <S.Row gutter={24} $marginTop={24}>
                  <Col span={24}>
                    <FormItem
                      noStyle={isClient || isAdmin}
                      label="Zip code"
                      name="zipCode"
                      validateTrigger={['onChange']}
                      rules={[
                        {
                          required: isVetter,
                          message: 'Zip code is required',
                        },
                      ]}
                    >
                      {isVetter && <Input size="large" />}
                    </FormItem>
                  </Col>
                </S.Row>
                {!isClientManager && (
                  <S.Row gutter={24} $marginTop={24}>
                    <Col span={24}>
                      <FormItem
                        label="Conduct interviews in"
                        name="language"
                        validateTrigger={['onChange']}
                        rules={[
                          {
                            required: true,
                            message: 'Conduct interviews in is required',
                          },
                        ]}
                      >
                        <Select
                          mode="multiple"
                          showArrow={true}
                          id="language"
                          option={['English', 'Spanish']}
                          size="large"
                        />
                      </FormItem>
                    </Col>
                  </S.Row>
                )}
              </Col>
              <Col span={12}>
                <FormItem
                  noStyle={isClientManager}
                  label="Profile photo"
                  name="photoOriginal"
                  tooltip="Upload an image file of type JPG, JPEG, PNG, GIF or WEBP and size less than 20MB."
                  rules={[
                    {
                      required: !isClientManager,
                      message: 'Profile picture is required',
                    },
                  ]}
                  ignoreNormalize
                >
                  {!isClientManager && (
                    <S.AvatarWrapper
                      $isEmpty={!photo?.url}
                      onClick={!photo?.url ? open : noop}
                    >
                      <Col>
                        <Row align="middle" gutter={8}>
                          <Col>
                            <S.Avatar
                              src={photo?.url || '/images/avatar.svg'}
                            />
                          </Col>
                          <Col>
                            {!photo?.url ? (
                              <S.UploadButton type="text">
                                Upload
                              </S.UploadButton>
                            ) : (
                              <Text>image.png</Text>
                            )}
                          </Col>
                        </Row>
                      </Col>
                      <Col>
                        {!!photo?.url && (
                          <S.DeleteIcon
                            onClick={() =>
                              form.setFieldsValue({
                                photoOriginal: null,
                                photoHigh: null,
                                photoLow: null,
                              })
                            }
                          />
                        )}
                      </Col>
                    </S.AvatarWrapper>
                  )}
                </FormItem>
              </Col>
            </S.Row>
          </S.Section>
          <S.Section>
            <S.TitleRow justify="space-between" align="middle">
              <Title level={2}>Contact Information</Title>
            </S.TitleRow>
            <S.Row gutter={72}>
              <Col span={12}>
                <FormItem
                  label="Email"
                  name="email"
                  validateTrigger={['onChange']}
                  rules={[
                    { required: true, message: 'Email address is required' },
                    { type: 'email', message: 'Must be a valid email address' },
                  ]}
                >
                  <Input type="email" size="large" />
                </FormItem>
              </Col>
              <Col span={12}>
                <S.Row justify="space-between" gutter={[24, 24]}>
                  <Col flex="auto">
                    <FormItem label="Phone number" name="phone">
                      <Input size="large" disabled={true} />
                    </FormItem>
                  </Col>
                  <Col flex="154px" style={{ alignSelf: 'end' }}>
                    <UpdatePhoneNumber settingsForm={form} />
                  </Col>
                </S.Row>
              </Col>
            </S.Row>
          </S.Section>
          <S.Section>
            <S.TitleRow justify="space-between" align="middle">
              <Title level={2}>Notifications</Title>
            </S.TitleRow>
            <S.Row justify="space-between" align="bottom">
              <Col flex="none">
                <S.NotificationSwitch
                  checked={pushNotificationsEnabled}
                  onChange={updatePushNotificationEnabled}
                />
              </Col>
              <Col flex="auto">
                <Title level={5}>Desktop push notifications</Title>
              </Col>
            </S.Row>
            <S.Row $marginTop={20}>
              <Col span={8}>
                <Text $marginBottom={20}>
                  Enable notifications if you want us to inform you every time
                  there is an incoming Vette.
                </Text>
              </Col>
            </S.Row>
          </S.Section>
        </S.Content>
      </PageCard>
    </S.Form>
  );
};
